import {
  AuthService,
  ConfigStateService,
  PermissionService,
  ReplaceableComponentsService,
  RoutesService,
} from '@abp/ng.core';
import { SettingTabsService } from '@abp/ng.setting-management/config';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { LoaderBarVisibilityService } from '@proxy/loader-bar-visibility.service';
import {
  PremiumReportingAddMenuService,
  PremiumReportingConfigService,
  PremiumReportingMenuService,
} from '@sestek/premium-reporting/config';
import { OAuthService } from 'angular-oauth2-oidc';
import { Subscription } from 'rxjs';
import { BotHubSettingsComponent } from './settings/bothub-settings/bothub-settings.component';
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { TOOLTIP_DELAY } from './shared/shared.consts';
import { CaIntegrationSettingsComponent } from './settings/ca-integration-settings/ca-integration-settings.component';
import { NotificationService } from '@sestek/notification';
import { FooterComponent } from './shared/footer/footer.component';
import { eThemeLeptonComponents } from '@volo/abp.ng.theme.lepton';
import { AddOnAIFeaturesSettingsComponent } from './settings/addon-ai-features-settings/addon-ai-features-settings.component';
import {
  AI_FEATURE_CONVERSATION_SUMMARIZATION,
  AI_FEATURE_GENERATIVE_QUESTION_ANSWERING,
  AI_FEATURE_LIVECHAT_SUMMARIZATION,
  AI_FEATURE_TEXT_RESPONSE_GENERATION,
  AI_FEATURE_UTTERANCE_GENERATION,
} from './virtual-agents/proxy/open-ai/open-ai.const';
import { LogoComponent } from './shared/nav-items/logo/logo.component';
import { NavigationEnd, Router } from '@angular/router';
import { RoutesComponent } from './shared/routes/cai-routes/cai-routes.component';
import { ToasterService } from '@abp/ng.theme.shared';
import { RedactionSettingsComponent } from './settings/redaction-settings/redaction-settings.component';
import { OutboundConversationSettingsComponent } from './settings/outbound-conversation-settings/outbound-conversation-settings.component';
import { OUTBOUND_FEATURE_OUTBOUND_CONVERSATIONS } from './projects/proxy/outbound/outbound-rules.const';
import { GenerativeQnaSettingsSettingsComponent } from './settings/generative-qna-settings/generative-qna-settings.component';
import { eIdentitySettingTabNames } from '@volo/abp.ng.identity/config';
import { CustomIdentitySettingsComponent } from './settings/cai-identity-settings/identity-settings.component';
import { AiTestingSettingsComponent } from './settings/ai-testing-settings/ai-testing-settings.component';
import { AGENTIC_AI_FEATURE } from './ai-agents/proxy/ai-agents.consts';
import { CaiConfigService } from './shared/services/cai-config.service';
import { BrandingSettingsComponent } from './settings/branding-settings/branding-settings.component';
import { BrandingSettingsService } from './settings/branding-settings/proxy/branding-settings.service';
import { BrandingSettingsHelperService } from './shared/services/branding-settings-helper.service';

@Component({
  selector: 'cai-root',
  template: `
    @if (isLoaderVisible) {
      <abp-loader-bar></abp-loader-bar>
    }
    <abp-dynamic-layout></abp-dynamic-layout>
    <cai-column-configurator></cai-column-configurator>
  `,
  providers: [NgbTooltipConfig],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  isLoaderVisible = true;
  loaderBarVisibilitySubscription: Subscription;

  constructor(
    private replaceableComponents: ReplaceableComponentsService,
    private loaderBarVisibilityService: LoaderBarVisibilityService,
    private oAuthService: OAuthService,
    private authService: AuthService,
    private settingTabs: SettingTabsService,
    private premiumReportingConfigService: PremiumReportingConfigService,
    private premiumReportingMenuService: PremiumReportingMenuService,
    private premiumReportingAddMenuService: PremiumReportingAddMenuService,
    private notificationService: NotificationService,
    private permissionService: PermissionService,
    private config: ConfigStateService,
    private caiConfigService: CaiConfigService,
    private ngbTooltipConfig: NgbTooltipConfig,
    private router: Router,
    private routes: RoutesService,
    private toaster: ToasterService,
    private brandingSettingsService: BrandingSettingsService,
    private brandingSettingsHelper: BrandingSettingsHelperService,
  ) {
    Date.prototype.toJSON = function () {
      const copiedDate = new Date(this.getTime());
      const result = copiedDate.toISOString();
      return result;
    };
    this.ngbTooltipConfig.openDelay = TOOLTIP_DELAY;

    this.notificationService.initialize();
  }

  get hasLoggedIn(): boolean {
    return this.oAuthService.hasValidAccessToken();
  }

  ngOnInit() {
    this.loadTableauScript();
    this.loadFavicon();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const navigationState = this.router.getCurrentNavigation().extras.state;
        if (!navigationState?.persistToaster) {
          this.toaster.clear();
        }
      }
    });

    const isPremiumReportingFeatureEnabled = this.caiConfigService.isFeatureEnabled(
      'PremiumReportingGroup.PremiumReporting',
    );
    if (
      this.permissionService.getGrantedPolicy('PremiumReporting.PremiumReports') &&
      isPremiumReportingFeatureEnabled
    ) {
      this.getPremiumReportingMenuList();
      this.premiumReportingMenuService.premiumReportingMenuChange.subscribe(() =>
        this.getPremiumReportingMenuList(),
      );
    }
    this.settingTabs.add([
      {
        name: '::BotHubSettings',
        order: 3,
        component: BotHubSettingsComponent,
        requiredPolicy: 'BotHub.SettingManagement',
      },
      {
        name: 'Administration::CaIntegrationSettings',
        order: 4,
        component: CaIntegrationSettingsComponent,
        requiredPolicy: 'AbpIdentity.SettingManagement',
      },
    ]);

    const isGenerativeQAFeatureEnabled = this.caiConfigService.isFeatureEnabled(
      AI_FEATURE_GENERATIVE_QUESTION_ANSWERING,
    );

    if (isGenerativeQAFeatureEnabled) {
      this.settingTabs.add([
        {
          name: 'Administration::GenerativeQuestionAnsweringSettings',
          order: 5,
          component: GenerativeQnaSettingsSettingsComponent,
          requiredPolicy: 'AbpIdentity.SettingManagement',
        },
      ]);
    }

    this.settingTabs.remove([eIdentitySettingTabNames.IdentityManagement]);

    this.settingTabs.add([
      {
        name: 'Administration::IdentityManagement',
        order: 6,
        component: CustomIdentitySettingsComponent,
        requiredPolicy: 'AbpIdentity.SettingManagement',
      },
    ]);

    const isAddOnAiFeaturesEnabled =
      this.caiConfigService.isFeatureEnabled(AI_FEATURE_CONVERSATION_SUMMARIZATION) ||
      this.caiConfigService.isFeatureEnabled(AI_FEATURE_UTTERANCE_GENERATION) ||
      this.caiConfigService.isFeatureEnabled(AI_FEATURE_LIVECHAT_SUMMARIZATION) ||
      this.caiConfigService.isFeatureEnabled(AI_FEATURE_TEXT_RESPONSE_GENERATION);

    const isLlmConfigurationPermissionGranted = this.permissionService.getGrantedPolicy(
      'Designer.LlmConfigurations',
    );
    if (isAddOnAiFeaturesEnabled && isLlmConfigurationPermissionGranted) {
      this.settingTabs.add([
        {
          name: 'Administration::AddOnAIFeatureSettings',
          order: 6,
          component: AddOnAIFeaturesSettingsComponent,
          requiredPolicy: 'AbpIdentity.SettingManagement',
        },
      ]);
    }

    this.settingTabs.add([
      {
        name: 'Administration::RedactionSettings',
        order: 7,
        component: RedactionSettingsComponent,
        requiredPolicy: 'AbpIdentity.SettingManagement',
      },
    ]);

    const isOutboundConversationsFeatureEnabled = this.caiConfigService.isFeatureEnabled(
      OUTBOUND_FEATURE_OUTBOUND_CONVERSATIONS,
    );

    if (isOutboundConversationsFeatureEnabled) {
      this.settingTabs.add([
        {
          name: 'Administration::OutboundSettings',
          order: 8,
          component: OutboundConversationSettingsComponent,
          requiredPolicy: 'AbpIdentity.SettingManagement',
        },
      ]);
    }

    // BrandingSettings is only available for Host (not for Tenants)
    const currentTenant = this.config.getOne('currentTenant');
    if (!currentTenant?.id) {
      this.settingTabs.add([
        {
          name: '::BrandingSettings',
          order: 9,
          component: BrandingSettingsComponent,
          requiredPolicy: 'AbpIdentity.SettingManagement',
        },
      ]);
    }

    const isAgenticAiFeatureEnabled = this.caiConfigService.isFeatureEnabled(AGENTIC_AI_FEATURE);

    if (isAgenticAiFeatureEnabled) {
      this.settingTabs.add([
        {
          name: 'Administration::AiTestingSettings',
          order: 20,
          component: AiTestingSettingsComponent,
          requiredPolicy: 'AbpIdentity.SettingManagement',
        },
      ]);
    }

    if (!this.hasLoggedIn) {
      this.authService.navigateToLogin();
    }

    this.replaceableComponents.add({
      component: FooterComponent,
      key: eThemeLeptonComponents.ApplicationLayoutFooter,
    });

    this.replaceableComponents.add({
      component: LogoComponent,
      key: eThemeLeptonComponents.Logo,
    });

    this.replaceableComponents.add({
      component: RoutesComponent,
      key: eThemeLeptonComponents.Routes,
    });

    this.loaderBarVisibilitySubscription =
      this.loaderBarVisibilityService.loaderBarVisibilityChanged.subscribe((isVisible: boolean) => {
        this.isLoaderVisible = isVisible;
      });
  }

  private loadTableauScript() {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = 'assets/js/tableau/tableau.embedding.3.2.0.min.js';
    document.head.appendChild(script);
  }

  private loadFavicon() {
    this.brandingSettingsService.get().subscribe({
      next: (settings) => {
        if (settings.faviconBase64 && settings.faviconContentType) {
          const dataUrl = this.brandingSettingsHelper.createDataUrl(
            settings.faviconBase64,
            settings.faviconContentType,
          );
          this.brandingSettingsHelper.updateBrowserFavicon(dataUrl);
        }
      },
      error: (err) => {
        console.error('Error loading favicon:', err);
      },
    });
  }

  getPremiumReportingMenuList() {
    this.premiumReportingConfigService.get({ maxResultCount: 50 }).subscribe((res) => {
      this.premiumReportingAddMenuService.addMenu(res.items);
      const prMenu = {
        order: 7,
      };
      this.routes.patch('PremiumReporting::Menu:PremiumReports', prMenu);
    });
  }

  ngOnDestroy() {
    this.loaderBarVisibilitySubscription.unsubscribe();
  }
}
